import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Plus,
  Search,
  Users,
  Loader2,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { CertificationHealthBadge } from '@/app/pages/crew/certification-health';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  useCrewLists,
  useCrewList,
  useCreateCrewList,
  useAddCrewListMember,
  useRemoveCrewListMember,
  useTechProfiles,
} from '@/hooks/use-crew';
import { handleApiError } from '@/lib/api-client';
import type { TechCertStatus } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const certStatusColors: Record<TechCertStatus, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-800',
};

export function CrewListsPage() {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCrewLists({ q: search || undefined });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <List className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Crew Lists</h1>
          </div>
          <p className="text-gray-600 mt-1">Manage reusable crew groups</p>
        </div>
        {canManageCrew && (
          <CreateCrewListDialog open={createOpen} onOpenChange={setCreateOpen} />
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search crew lists..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : !data || data.results.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">No crew lists found</p>
            {canManageCrew && (
              <Button variant="outline" className="mt-4" onClick={() => setCreateOpen(true)}>
                Create your first crew list
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.results.map((list) => (
            <Card
              key={list.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${
                selectedListId === list.id ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedListId(selectedListId === list.id ? null : list.id)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>{list.name}</span>
                  <Badge variant="outline">{list.member_ids?.length ?? 0} members</Badge>
                </CardTitle>
                {list.description && (
                  <p className="text-sm text-gray-600">{list.description}</p>
                )}
              </CardHeader>
              <CardContent>
                {list.members && list.members.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {list.members.slice(0, 5).map((member) => (
                      <div key={member.id} className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2 py-1">
                        {member.photo ? (
                          <img
                            src={member.photo}
                            alt={member.full_name}
                            className="h-5 w-5 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 text-xs font-semibold">
                              {member.first_name[0]}
                            </span>
                          </div>
                        )}
                        <span className="text-xs font-medium">{member.full_name}</span>
                        <CertificationHealthBadge
                          certifications={member.certification_summary}
                          compact
                        />
                      </div>
                    ))}
                    {list.members.length > 5 && (
                      <span className="text-xs text-gray-500 self-center">
                        +{list.members.length - 5} more
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No members yet</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedListId && <CrewListDetail listId={selectedListId} />}
    </div>
  );
}

function CreateCrewListDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createMutation = useCreateCrewList();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await createMutation.mutateAsync({ name: name.trim(), description: description.trim() });
      onOpenChange(false);
      setName('');
      setDescription('');
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create List
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Crew List</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
          <div>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Project Alpha Team" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional description"
            />
          </div>
          <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create List
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CrewListDetail({ listId }: { listId: number }) {
  const { data: list, isLoading } = useCrewList(listId);
  const addMemberMutation = useAddCrewListMember();
  const removeMemberMutation = useRemoveCrewListMember();
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const [techSearch, setTechSearch] = useState('');
  const { data: searchResults } = useTechProfiles({
    q: techSearch || undefined,
    page_size: 5,
    status: 'active',
  });

  const handleAddMember = async (techId: number) => {
    await addMemberMutation.mutateAsync({ listId, techId });
    setTechSearch('');
  };

  const handleRemoveMember = async (techId: number) => {
    await removeMemberMutation.mutateAsync({ listId, techId });
  };

  if (isLoading) return <Skeleton className="h-48 mt-4" />;
  if (!list) return null;

  const memberIds = new Set(list.member_ids ?? []);
  const filteredSearchResults = searchResults?.results.filter(
    (t) => !memberIds.has(t.id)
  );

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{list.name} — Members</span>
          <Badge variant="outline">{list.members?.length ?? 0} members</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canManageCrew && (
          <div className="relative">
            <UserPlus className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search technicians to add..."
              value={techSearch}
              onChange={(e) => setTechSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        {canManageCrew && techSearch && filteredSearchResults && filteredSearchResults.length > 0 && (
          <div className="border rounded-lg divide-y">
            {filteredSearchResults.map((tech) => (
              <div key={tech.id} className="flex items-center justify-between p-2 hover:bg-gray-50">
                <span className="text-sm font-medium">{tech.full_name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddMember(tech.id)}
                  disabled={addMemberMutation.isPending}
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {list.members && list.members.length > 0 ? (
          <div className="space-y-2">
            {list.members.map((member) => (
              <div key={member.id} className="flex items-center justify-between border rounded-lg p-3">
                <Link to={`/crew/techs/${member.id}`} className="flex items-center gap-3">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.full_name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-xs">
                        {member.first_name[0]}{member.last_name[0]}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-sm">{member.full_name}</p>
                    <div className="flex gap-1 mt-0.5">
                      {member.certification_summary?.map((cert, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className={`text-xs py-0 ${certStatusColors[cert.status] ?? ''}`}
                        >
                          {cert.cert_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Link>
                {canManageCrew && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${member.full_name}`}
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={removeMemberMutation.isPending}
                  >
                    <UserMinus className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No members in this list</p>
        )}
      </CardContent>
    </Card>
  );
}
